import {
    AsOfBadge,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    StatusBadge,
    statusLabel,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { StaffOrderRow } from '@/api/types';
import { cardLabel } from '@/components/charge';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/orders';
const PAGE_KEY = 'orders';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const ordersPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const orderFilters = filterDefs(PATH, {
    status: 'Status',
    source: 'Source',
    merchant_id: 'Merchant',
    store_id: 'Store',
    buyer_email: 'Buyer email',
    q: 'Search',
    range: 'Created',
});

/** The order page (bm-147 § 3). */
export const orderHref = (id: string) => `/orders/${encodeURIComponent(id)}`;

const money = (cents: number | undefined, currency: string) =>
    cents === undefined ? '—' : <Money cents={cents} currency={currency} />;

/**
 * bm-147 § 1: every merchant's orders, newest first (the list contract's default), with the 14 columns of the shared
 * order shape; Export downloads through the staff orders export route.
 */
export function OrdersPage() {
    const columns: ListColumnDef<StaffOrderRow>[] = [
        {
            key: 'id',
            label: 'Order',
            cell: (r) => (
                <Link
                    to={orderHref(r.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.id}
                </Link>
            ),
            csv: (r) => r.id,
            boardCard: true,
            noTruncate: true,
        },
        {
            key: 'charge_id',
            aliases: ['charge'],
            label: 'Charge',
            csv: (r) => r.charge_id,
            cell: (r) => <ObjectRef id={r.charge_id} />,
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            key: 'store',
            label: 'Store',
            csv: (r) => r.store_id ?? '',
            cell: (r) =>
                r.store ? (
                    <ObjectRef
                        id={r.store.id}
                        label={r.store.display_name ?? r.store.name}
                        href={`/stores/${encodeURIComponent(r.store.id)}`}
                    />
                ) : (
                    '—'
                ),
        },
        {
            key: 'card',
            label: 'Card',
            csv: (r) => (r.card ? cardLabel(r.card) : ''),
            cell: (r) => (r.card ? cardLabel(r.card) : '—'),
        },
        {
            key: 'source',
            label: 'Source',
            sortKey: 'source',
            csv: (r) => r.source,
            cell: (r) => statusLabel(r.source),
        },
        {
            key: 'status',
            label: 'Status',
            sortKey: 'status',
            csv: (r) => r.status,
            boardCard: true,
            cell: (r) => <StatusBadge status={r.status} domain="order" />,
            groupable: { key: 'status', statusDomain: 'order' },
        },
        {
            key: 'subtotal_cents',
            aliases: ['base'],
            label: 'Base',
            sortKey: 'subtotal_cents',
            csv: (r) => r.subtotal_cents,
            cell: (r) => money(r.subtotal_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'tax_cents',
            aliases: ['tax'],
            label: 'Tax',
            csv: (r) => r.tax_cents,
            cell: (r) => money(r.tax_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'total_cents',
            aliases: ['total'],
            label: 'Total',
            sortKey: 'total_cents',
            csv: (r) => r.total_cents,
            boardCard: true,
            cell: (r) => money(r.total_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'fee_cents',
            aliases: ['fee'],
            label: 'Fee',
            csv: (r) => r.fee_cents ?? '',
            cell: (r) => money(r.fee_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'reserve_cents',
            aliases: ['reserve'],
            label: 'Reserve',
            csv: (r) => r.reserve_cents ?? '',
            cell: (r) => money(r.reserve_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'refunded_cents',
            aliases: ['refunded'],
            label: 'Refunded',
            sortKey: 'refunded_cents',
            csv: (r) => r.refunded_cents ?? '',
            cell: (r) => money(r.refunded_cents, r.currency),
            noTruncate: true,
        },
        {
            key: 'captured_at',
            aliases: ['paid_at'],
            label: 'Paid at',
            kind: 'datetime',
            sortKey: 'captured_at',
            csv: (r) => r.captured_at,
            cell: (r) => <DateTime value={r.captured_at} empty="—" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<StaffOrderRow>(PAGE_KEY, PATH, {
        columns,
        filters: orderFilters,
    });
    return (
        <div>
            <PageHeader
                title="Orders"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Paid orders across merchants, with their charge,
                            amounts, fee and reserve.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="orders"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                rowHref={(r) => orderHref(r.id)}
                controls={{ status: { domain: 'order' } }}
                mobileCard={{
                    fields: ['status', 'total_cents'],
                }}
            />
        </div>
    );
}
